import { type FormEvent, useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';

import { api, ApiError } from '../lib/api-client';
import { resetSessionCache } from '../lib/query-client';
import { useAuthStore } from '../stores/auth-store';

/**
 * Tela de login. Form com email + senha; tenantSlug derivado do subdomínio
 * (X-Tenant-Slug) pelo apiClient. Aplica classes do components.css
 * (DS v2.1 — `.input`, `.btn--primary`).
 */
export function LoginPage(): JSX.Element {
  const isAuthenticated = useAuthStore((s) => s.accessToken !== null);
  const setSession = useAuthStore((s) => s.setSession);
  const setMe = useAuthStore((s) => s.setMe);

  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Lume — Entrar';
  }, []);

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const login = await api.login({ email, password });
      setSession(login);
      // Transição de identidade: zera qualquer server-state cacheado de uma
      // sessão anterior ANTES de carregar o /auth/me e telas do novo usuário.
      // Garante slate limpo mesmo que o logout anterior não tenha rodado.
      resetSessionCache();
      const me = await api.me();
      setMe(me);
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError('Credenciais inválidas.');
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Erro inesperado ao entrar.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-app-bg px-4">
      <div className="w-full max-w-sm space-y-6 rounded-lg bg-surface p-8 shadow-md border border-border-subtle">
        <div className="space-y-1 text-center">
          <h1 className="text-xl font-semibold tracking-tight text-text-primary">
            Lume Atendimento
          </h1>
          <p className="text-sm text-text-secondary">Entre com suas credenciais para continuar.</p>
        </div>

        <form className="space-y-4" onSubmit={(e) => void handleSubmit(e)} noValidate>
          <label className="block space-y-1">
            <span className="text-sm font-medium text-text-primary">E-mail</span>
            <input
              type="email"
              required
              autoComplete="username"
              className="input w-full"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
            />
          </label>

          <label className="block space-y-1">
            <span className="text-sm font-medium text-text-primary">Senha</span>
            <input
              type="password"
              required
              autoComplete="current-password"
              className="input w-full"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={submitting}
            />
          </label>

          {error !== null && (
            <p role="alert" className="text-sm text-red-600">
              {error}
            </p>
          )}

          <button
            type="submit"
            className="btn btn--primary w-full"
            disabled={submitting || email === '' || password === ''}
          >
            {submitting ? 'Entrando…' : 'Entrar'}
          </button>
        </form>

        <p className="text-center text-sm">
          <Link to="/esqueci-senha" className="text-text-secondary underline underline-offset-2">
            Esqueci minha senha
          </Link>
        </p>
      </div>
    </div>
  );
}
